import React, { useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Phone, FileText, ChevronDown,
  Sparkles, MapPin, Clock, Shield, Award,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReservationForm from '../components/ReservationForm';
import { Button } from '../components/ui/button';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import { LOCAL_PAGES } from '../data/localPages';

const SITE_ORIGIN = 'https://smt-services17.fr';

/**
 * Generic local-SEO page. Reads its configuration from `LOCAL_PAGES`
 * keyed by the current pathname slug (so routes can be declared as static
 * paths in App.js — no :slug param needed).
 */
const LocalSeoPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const cfg = LOCAL_PAGES[slug];
  const [openFaq, setOpenFaq] = useState(0);

  // Always call hooks unconditionally (avoid React hooks order violation).
  useDocumentMeta({
    title: cfg?.title || 'SMT Services',
    description: cfg?.description || '',
    path: cfg ? `/${cfg.slug}` : '/',
    image: cfg?.heroImage,
    ogType: 'website',
    jsonLd: cfg
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'LocalBusiness',
              '@id': `${SITE_ORIGIN}/${cfg.slug}#business`,
              name: 'SMT Services',
              image: cfg.heroImage,
              url: `${SITE_ORIGIN}/${cfg.slug}`,
              telephone: '+33625684849',
              email: 'smt.services17t@gmail.com',
              priceRange: '€€',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Chaillevette',
                postalCode: '17890',
                addressRegion: 'Charente-Maritime',
                addressCountry: 'FR',
              },
              areaServed: { '@type': 'City', name: cfg.city },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4.8',
                reviewCount: '28',
                bestRating: '5',
                worstRating: '1',
              },
            },
            {
              '@type': 'Service',
              '@id': `${SITE_ORIGIN}/${cfg.slug}#service`,
              serviceType: cfg.serviceType,
              name: `${cfg.service} à ${cfg.city}`,
              description: cfg.description,
              areaServed: { '@type': 'City', name: cfg.city },
              provider: { '@id': `${SITE_ORIGIN}/${cfg.slug}#business` },
              offers: {
                '@type': 'Offer',
                priceCurrency: 'EUR',
                priceSpecification: {
                  '@type': 'PriceSpecification',
                  priceCurrency: 'EUR',
                  description: `À partir de ${cfg.priceRange}`,
                },
              },
            },
            {
              '@type': 'FAQPage',
              mainEntity: cfg.faq.map((it) => ({
                '@type': 'Question',
                name: it.q,
                acceptedAnswer: { '@type': 'Answer', text: it.a },
              })),
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE_ORIGIN}/` },
                { '@type': 'ListItem', position: 2, name: cfg.h1, item: `${SITE_ORIGIN}/${cfg.slug}` },
              ],
            },
          ],
        }
      : null,
  });

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid={`local-page-${cfg.slug}`}>
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-blue-50 via-slate-50 to-orange-50 pt-32 pb-12 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-30" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <nav aria-label="Fil d'Ariane" className="mb-5 text-sm">
              <ol className="flex flex-wrap items-center gap-2 text-slate-500">
                <li>
                  <Link to="/" className="hover:text-[hsl(207,90%,44%)]" data-testid="breadcrumb-home">
                    Accueil
                  </Link>
                </li>
                <li aria-hidden="true">›</li>
                <li className="text-slate-900 font-medium">{cfg.h1}</li>
              </ol>
            </nav>

            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[hsl(207,90%,54%)] text-white px-3 py-1.5 rounded-full font-semibold text-xs mb-5 shadow-md">
                  <MapPin className="w-3.5 h-3.5" /> {cfg.city} · Charente-Maritime
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">
                  {cfg.h1}
                </h1>
                <p className="text-base sm:text-lg text-gray-700 mb-7 leading-relaxed">
                  {cfg.intro}
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <Button asChild size="lg" className="bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-bold shadow-lg">
                    <a href="#devis-local" data-testid="hero-cta-devis">
                      <FileText className="w-4 h-4 mr-2" /> Demander un devis gratuit
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-2 font-semibold">
                    <a href="tel:+33625684849" data-testid="hero-cta-call">
                      <Phone className="w-4 h-4 mr-2" /> 06 25 68 48 49
                    </a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Devis sous 24h
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Crédit d'impôt 50 %
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> 4,8/5 sur Google
                  </span>
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                <img
                  src={cfg.heroImage}
                  alt={cfg.heroImageAlt}
                  className="w-full h-auto object-cover aspect-[4/3]"
                  loading="eager"
                  fetchpriority="high"
                  data-testid="local-hero-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
            Pourquoi choisir SMT Services à {cfg.city} ?
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Une équipe locale, du matériel professionnel, et la garantie d'un résultat impeccable.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {cfg.whyUs.map((item, i) => {
              const icons = [Award, Sparkles, Shield, Clock];
              const Icon = icons[i % icons.length];
              return (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[hsl(207,90%,54%)]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[hsl(207,90%,44%)]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Process */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">
              Comment se déroule l'intervention à {cfg.city}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {cfg.process.map((step, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="text-base font-bold text-[hsl(207,90%,44%)] mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-center">
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-5 border border-blue-100">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Tarif indicatif</p>
                <p className="text-2xl font-bold text-gray-900">{cfg.priceRange}</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-slate-50 rounded-2xl p-5 border border-emerald-100">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Délai</p>
                <p className="text-2xl font-bold text-gray-900">{cfg.delays.split(',')[0]}</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-slate-50 rounded-2xl p-5 border border-orange-100">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Économie</p>
                <p className="text-2xl font-bold text-emerald-600">- 50 %</p>
              </div>
            </div>
          </div>
        </section>

        {/* Zones desservies */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
            Zones d'intervention autour de {cfg.city}
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Nous intervenons gratuitement dans tout le secteur — pas de frais de déplacement supplémentaire.
          </p>
          <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
            {cfg.zones.map((z) => (
              <span key={z} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-4 py-2 rounded-full text-sm text-gray-700 shadow-sm">
                <MapPin className="w-3.5 h-3.5 text-[hsl(207,90%,54%)]" /> {z}
              </span>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
              Questions fréquentes — {cfg.service} à {cfg.city}
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              Tout ce que nos clients de {cfg.city} nous demandent avant de réserver.
            </p>
            <div className="space-y-3">
              {cfg.faq.map((item, idx) => {
                const open = openFaq === idx;
                return (
                  <div key={idx} className="bg-slate-50 hover:bg-slate-100/70 rounded-2xl border border-slate-200 overflow-hidden transition-colors">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : idx)}
                      className="w-full text-left flex items-center justify-between gap-4 p-5 sm:p-6 cursor-pointer"
                      data-testid={`local-faq-q-${idx}`}
                      aria-expanded={open}
                    >
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">{item.q}</h3>
                      <ChevronDown className={`flex-shrink-0 w-5 h-5 text-[hsl(207,90%,54%)] transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                      <div className="overflow-hidden">
                        <p className="px-5 sm:px-6 pb-5 sm:pb-6 text-gray-700 text-sm sm:text-base leading-relaxed" data-testid={`local-faq-a-${idx}`}>
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Maillage interne */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
            Pour aller plus loin
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {cfg.relatedLinks.map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className="group flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl px-5 py-4 hover:border-[hsl(207,90%,54%)] hover:shadow-md transition-all"
                data-testid={`related-link-${i}`}
              >
                <span className="text-gray-800 font-medium text-sm sm:text-base">{link.label}</span>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[hsl(207,90%,54%)] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </section>

        {/* Formulaire de devis intégré */}
        <div id="devis-local" />
        <ReservationForm />
      </main>
      <Footer />
    </div>
  );
};

export default LocalSeoPage;
